<template>
  <div v-if="visible || sinComprobar" class="resultado-llamada">
    <div class="resultado-llamada__titulo">Resultado de la llamada</div>

    <template v-if="sinComprobar">
      <p class="resultado-llamada__error">
        No se ha podido comprobar si esta tarea es una llamada con resultado que anotar ({{ errorCarga }}).
        <template v-if="obligatorio">
          Hasta que se compruebe no se puede guardar como REALIZADA.
        </template>
        Pulsa «Reintentar»; si sigue fallando, avisa a soporte.
      </p>
      <f7-button class="resultado-llamada__boton" fill small :disabled="!cargado" @click="cargar">
        Reintentar
      </f7-button>
    </template>

    <p v-else-if="!hayListas" class="resultado-llamada__nota">
      No hay valores configurados para anotar la llamada. Los configura Dirección en CRM-NEW →
      Configuración → Tipos de tarea. Mientras tanto la tarea se puede cerrar igual.
    </p>

    <template v-else-if="enEdicion">
      <div class="resultado-llamada__campos">
        <label v-for="c in campos" :key="c.clave" class="resultado-llamada__campo">
          <span>{{ c.titulo }}<span v-if="obligatorio"> *</span></span>
          <span class="input input-outline input-dropdown resultado-llamada__desplegable">
            <select v-model="seleccion[c.clave]" :disabled="guardando">
              <option value="" disabled>— elige —</option>
              <option v-for="o in opciones(c)" :key="o.codigo" :value="o.codigo">{{ o.etiqueta }}</option>
            </select>
          </span>
        </label>
      </div>
      <p v-if="obligatorio && !completo" class="resultado-llamada__nota">
        Obligatorio al marcar la tarea como REALIZADA: elige el resultado, el motivo y el siguiente paso.
      </p>
      <template v-if="readonly">
        <p v-if="!info.anotacion" class="resultado-llamada__nota">
          Falta anotar esta llamada. Elige las tres cosas y pulsa «Guardar resultado».
        </p>
        <f7-button class="resultado-llamada__boton" fill small :disabled="!completo || guardando" @click="guardar">
          Guardar resultado
        </f7-button>
        <p v-if="!completo" class="resultado-llamada__nota">Se activa al elegir las tres cosas.</p>
      </template>
      <p v-if="error" class="resultado-llamada__error">{{ error }}</p>
    </template>

    <template v-else>
      <div class="resultado-llamada__leido">
        <div v-for="c in campos" :key="c.clave">
          <span class="resultado-llamada__etiqueta">{{ c.titulo }}:</span>
          {{ info.anotacion[c.clave] ? info.anotacion[c.clave].etiqueta : '—' }}
        </div>
      </div>
      <p class="resultado-llamada__nota">Para corregirlo, pulsa «Editar» en el menú ⋮ de la tarea.</p>
    </template>
  </div>
</template>

<script>
import LlamadaAnotacion from "../../../js/db/LlamadaAnotacion.js";
import cerrojoDeLaSesion from "../../../js/utils/cerrojoDeLaSesion.js";
import { generacionActual, sigueLaGeneracion } from "../../../js/utils/sesionDePeticiones.js";

// #934 · Fase 0 del protocolo de llamadas (Dirección, 26/10): al cerrar una tarea de un tipo marcado
// como llamada, el comercial anota resultado, motivo y siguiente paso en listas cerradas. Sin audio,
// sin transcripción y sin texto libre. La anotación vive en el CRM: se guarda después de la tarea, con
// su propia petición, y la API vuelve a comprobar tipo, estado, ámbito y valores.
//
// Si la API no deja leerla (403: quien no tiene acceso al CRM, o una tarea fuera de su ámbito), el bloque
// no se dibuja y no se exige nada: el cierre de la tarea sigue siendo el de siempre. Cualquier otro fallo
// no dice eso, solo que no se sabe si es una llamada: tomarlo por «no aplica» dejaba cerrar una llamada
// sin anotar tras un 500 pasajero. Ahí se avisa, se puede reintentar y no se deja cerrarla.
const CAMPOS = [
  { clave: "resultado", titulo: "Resultado", lista: "resultados" },
  { clave: "motivo", titulo: "Motivo", lista: "motivos" },
  { clave: "siguientePaso", titulo: "Siguiente paso", lista: "siguientesPasos" }
];

export default {
  props: {
    idTarea: { type: String, required: true },
    tipoCodigo: { type: String, default: null },
    estado: { type: String, default: null },
    // Estado con el que se abrió la tarea: solo se exige anotar al pasarla a REALIZADA ahora, no al
    // editar una llamada que ya estaba cerrada antes de que existiera la anotación.
    estadoInicial: { type: String, default: null },
    readonly: { type: Boolean, default: true }
  },
  data() {
    return {
      campos: CAMPOS,
      info: null,
      seleccion: { resultado: "", motivo: "", siguientePaso: "" },
      guardando: false,
      error: null,
      // Mensaje del último fallo al cargar que no era falta de acceso; null si cargó o si no aplica.
      errorCarga: null,
      // Promesa de la carga en curso. Mientras no llega no se sabe si la tarea es una llamada, y la
      // ficha tiene que esperarla antes de dar por bueno un cierre (ver `listo`).
      carga: null,
      generacionDeLaCarga: null,
      cargado: false
    };
  },
  computed: {
    esLlamada() {
      return !!(this.info && this.tipoCodigo && (this.info.tiposLlamada || []).indexOf(this.tipoCodigo) !== -1);
    },
    visible() {
      return this.esLlamada && this.estado === "REALIZADA";
    },
    // Solo en las tareas REALIZADA, que es donde se anota: en las demás no cambia nada aunque falle.
    sinComprobar() {
      return !!this.errorCarga && this.estado === "REALIZADA";
    },
    hayListas() {
      return !!this.info && CAMPOS.every(c => (this.info[c.lista] || []).length > 0);
    },
    enEdicion() {
      return !this.readonly || !this.info.anotacion || !!this.error;
    },
    obligatorio() {
      return !this.readonly && this.estadoInicial !== "REALIZADA";
    },
    completo() {
      return CAMPOS.every(c => !!this.seleccion[c.clave]);
    },
    cambiado() {
      const a = this.info && this.info.anotacion;
      return CAMPOS.some(c => this.seleccion[c.clave] !== (a && a[c.clave] ? a[c.clave].codigo : ""));
    }
  },
  watch: {
    idTarea: { immediate: true, handler() { this.cargar(); } },
    readonly(v) {
      // Al cancelar la edición se vuelve a lo guardado. No mientras se guarda ni si falló: ahí lo
      // elegido es lo que hay que mandar o reintentar.
      if (v && !this.guardando && !this.error) this.copiarAnotacion();
    }
  },
  methods: {
    opciones(c) {
      const lista = (this.info && this.info[c.lista]) || [];
      const a = this.info && this.info.anotacion && this.info.anotacion[c.clave];
      // Un valor que Dirección retiró después de anotarlo sigue apareciendo en esta tarea.
      if (a && !lista.some(o => o.codigo === a.codigo)) return lista.concat([a]);
      return lista;
    },
    cargar() {
      this.error = null;
      this.errorCarga = null;
      const id = this.idTarea;
      if (!id) {
        this.info = null;
        this.cargado = true;
        this.carga = Promise.resolve();
        this.generacionDeLaCarga = generacionActual();
        return this.carga;
      }
      this.cargado = false;
      this.generacionDeLaCarga = generacionActual();
      const carga = LlamadaAnotacion.deTarea(id)
        .then(info => {
          // Si entretanto se ha pasado a otra tarea, esta respuesta ya no es de la que se ve.
          if (id !== this.idTarea) return;
          this.info = info && info.idTarea ? info : null;
          this.copiarAnotacion();
        })
        .catch(e => {
          if (id !== this.idTarea) return;
          this.info = null;
          if (!e || e.status !== 403) this.errorCarga = (e && e.message) || "error desconocido";
        })
        .finally(() => {
          if (carga === this.carga) this.cargado = true;
        });
      this.carga = carga;
      return carga;
    },
    // Para el `beforeSave` de la ficha: no rechaza nunca, y al resolverse `faltaAnotar` ya mira la
    // configuración de verdad y no el `info` vacío de antes de cargarla. Con `reintentar`, una carga
    // que falló se vuelve a pedir: un fallo pasajero no obliga a pulsar «Reintentar» antes de guardar.
    listo(reintentar) {
      if (reintentar && this.cargado && this.errorCarga) return this.cargar().then(() => this.listo());
      if (this.cargado) return Promise.resolve();
      // #663 · Si la sesión cambió con la carga en vuelo, la frontera la cancela sin correr su
      // `finally`: `cargado` no se pondría nunca y la ficha, que vive entre sesiones, no guardaría.
      const vigente = this.carga && sigueLaGeneracion(this.generacionDeLaCarga);
      return (vigente ? this.carga : this.cargar()).then(() => this.listo());
    },
    copiarAnotacion() {
      const a = this.info && this.info.anotacion;
      CAMPOS.forEach(c => {
        this.$set(this.seleccion, c.clave, a && a[c.clave] ? a[c.clave].codigo : "");
      });
    },
    // Para el `beforeSave` de la ficha: ¿se está cerrando la llamada sin anotarla?
    faltaAnotar() {
      return this.visible && this.hayListas && this.obligatorio && !this.completo;
    },
    // Y ¿se está cerrando sin haber podido saber si es una llamada?
    cierreSinComprobar() {
      return this.sinComprobar && this.obligatorio;
    },
    // Tras guardar la tarea. Si no hay nada nuevo que anotar, no llama a la API.
    guardarSiHaceFalta() {
      if (!this.visible || !this.hayListas || !this.completo || !this.cambiado) return Promise.resolve();
      return this.guardar();
    },
    guardar() {
      cerrojoDeLaSesion(this, "guardando");
      this.error = null;
      return LlamadaAnotacion.guardar(this.idTarea, {
        resultado: this.seleccion.resultado,
        motivo: this.seleccion.motivo,
        siguientePaso: this.seleccion.siguientePaso
      })
        .then(info => {
          this.info = info;
          this.copiarAnotacion();
        })
        .catch(e => {
          this.error = "No se ha guardado el resultado de la llamada: " +
            ((e && e.message) || "error desconocido") + ". Vuelve a pulsar «Guardar resultado».";
          throw e;
        })
        .finally(() => {
          this.guardando = false;
        });
    }
  }
};
</script>

<style scoped>
.resultado-llamada {
  margin: 12px 16px;
  padding: 10px 12px;
  border: 1px solid var(--f7-list-item-border-color, #ddd);
  border-radius: 6px;
}
.resultado-llamada__titulo {
  font-weight: bold;
  margin-bottom: 8px;
}
.resultado-llamada__campos {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.resultado-llamada__campo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
  flex: 1;
  font-size: 13px;
}
/* La ficha va dentro de una lista de F7, que fija a todo `select` la altura de `--f7-input-height`
   (32px con la letra de 18px de app.css): un relleno vertical propio le comía el hueco al texto y lo
   cortaba por arriba. Borde, flecha y relleno lateral los ponen las clases de F7 de un campo «outline»,
   como en Empleado o Fecha, y la altura es la de esos campos. */
.resultado-llamada__desplegable {
  display: block;
  --f7-input-height: var(--f7-input-outline-height);
}
.resultado-llamada__nota {
  margin: 6px 0 0;
  font-size: 12px;
  color: #666;
}
.resultado-llamada__error {
  margin: 6px 0 0;
  font-size: 12px;
  color: #c62828;
}
.resultado-llamada__boton {
  display: inline-flex;
  margin-top: 8px;
}
.resultado-llamada__etiqueta {
  color: #666;
}
</style>
